import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { ThemeToggle } from "@/components/shell/theme-toggle";

export function SignInPage() {
  const { signIn, status } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated") navigate("/", { replace: true });
  }, [status, navigate]);

  const handleSignIn = () => {
    try {
      setError(null);
      signIn();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign in failed");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-[1fr,420px]">
      <aside className="hidden md:flex md:flex-col md:justify-between md:bg-surface-muted md:px-8 md:py-7">
        <div className="flex items-center gap-2">
          <div className="grid h-7 w-7 place-items-center rounded-md bg-accent text-accent-fg text-sm font-semibold">
            O
          </div>
          <span className="text-sm font-semibold text-ink">OpenClaw Sales</span>
        </div>
        <div className="max-w-md">
          <h2 className="text-3xl font-semibold text-ink leading-tight">
            Sales control room for the SATashkent bot.
          </h2>
          <p className="mt-4 text-md text-ink-muted">
            Triage leads, respond to handoffs, and tune what the bot knows. Sign in with your SATashkent account.
          </p>
        </div>
        <div className="text-xs text-ink-faint">
          Internal tool. Access is provisioned automatically from SSO on first sign-in.
        </div>
      </aside>

      <section className="relative flex items-center justify-center px-6 py-10 md:px-10">
        <div className="absolute right-4 top-4">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-semibold text-ink">Sign in</h1>
          <p className="mt-2 text-sm text-ink-muted">
            We use single sign-on. You will be redirected to{" "}
            <span className="font-medium text-ink">auth.satashkent.uz</span>.
          </p>

          <Button
            variant="primary"
            size="lg"
            className="mt-7 w-full"
            onClick={handleSignIn}
            disabled={status === "loading"}
          >
            Continue with SSO
            <ArrowRight className="h-4 w-4" />
          </Button>

          {error && (
            <p role="alert" className="mt-3 text-sm text-danger">
              {error}
            </p>
          )}

          <p className="mt-6 text-xs text-ink-faint">
            Trouble signing in? Ask an existing admin to confirm your SSO account is provisioned for this workspace.
          </p>
        </div>
      </section>
    </div>
  );
}
